import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import ArticleHeader from '@/components/article/ArticleHeader';
import ArticleToolbar from '@/components/article/ArticleToolbar';
import ArticleLead from '@/components/article/ArticleLead';
import ArticleLoading from '@/components/article/ArticleLoading';
import ArticleError from '@/components/article/ArticleError';
import ArticleHeaderImage from '@/components/article/ArticleHeaderImage';
import ArticleFooter from '@/components/article/ArticleFooter';
import InstantTranslation from '@/components/article/InstantTranslation';
import EnhancedArticleContent from '@/components/article/EnhancedArticleContent';
import ArticleAIPanel from '@/components/article/ArticleAIPanel';
import ArticleBuyButton from '@/components/article/ArticleBuyButton';
import { useArticleData } from '@/hooks/useArticleData';
import { useTranslation, LANGUAGE_CODES, SupportedLanguage } from '@/hooks/useTranslation';
import { toast } from '@/hooks/use-toast';
import { ArticleAIProvider } from '@/contexts/article-ai/ArticleAIContext';
import { useArticleProgressService } from '@/hooks/reader-home/useArticleProgressService';
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from '@/hooks/useAuth';

const ArticleReader = () => {
  const {
    articleId
  } = useParams();
  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>('English');
  const {
    article,
    isLoading,
    error,
    isFollowing,
    isBookmarked,
    setIsFollowing,
    toggleBookmark
  } = useArticleData(articleId);
  const {
    translateText,
    translateBlockContent,
    isTranslating
  } = useTranslation();
  const [calculatedReadTime, setCalculatedReadTime] = useState<number | undefined>(undefined);
  const [translatedTitle, setTranslatedTitle] = useState<string | undefined>(undefined);
  const [translatedLead, setTranslatedLead] = useState<string | undefined>(undefined);
  const [translatedBlocks, setTranslatedBlocks] = useState<any[] | undefined>(undefined);
  const [translatedContent, setTranslatedContent] = useState<string | undefined>(undefined);
  const [hasScrolled, setHasScrolled] = useState<boolean>(false);
  const {
    updateArticleReadProgress
  } = useArticleProgressService();
  const {
    user
  } = useAuth();
  const readingRecorded = useRef<boolean>(false);

  useEffect(() => {
    if (!articleId || readingRecorded.current) return;
    const handleScroll = () => {
      if (!hasScrolled && !readingRecorded.current) {
        setHasScrolled(true);
        readingRecorded.current = true;
        updateArticleReadProgress(articleId, 1).then(() => {
          console.log('Reading progress recorded');
        }).catch(error => {
          console.error('Failed to record reading progress:', error);
        });
        window.removeEventListener('scroll', handleScroll);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [articleId, hasScrolled, updateArticleReadProgress]);

  useEffect(() => {
    if (article) {
      if (!article.readTime && !article.reading_time) {
        let contentText = '';
        if (article.content && typeof article.content === 'string') {
          contentText = article.content;
        } else if (article.blocks && article.blocks.length > 0) {
          contentText = article.blocks.map(block => {
            if (block.content && block.content.text) {
              return block.content.text;
            } else if (block.type === 'paragraph' && block.content) {
              return block.content.text || '';
            }
            return '';
          }).join(' ');
        } else if (article.editor_data && article.editor_data.blocks) {
          contentText = article.editor_data.blocks.map((block: any) => {
            if (block.data && block.data.text) {
              return block.data.text;
            }
            return '';
          }).join(' ');
        }
        contentText = contentText.replace(/<[^>]*>/g, '');
        const wordCount = contentText.split(/\s+/).filter(word => word.length > 0).length;
        const readTime = Math.max(1, Math.ceil(wordCount / 200));
        setCalculatedReadTime(readTime);
      } else {
        setCalculatedReadTime(article.readTime || article.reading_time || 1);
      }
    }
  }, [article]);

  useEffect(() => {
    const handleTranslation = async () => {
      if (!article || currentLanguage === 'English' || isTranslating) {
        if (currentLanguage === 'English') {
          setTranslatedTitle(undefined);
          setTranslatedLead(undefined);
          setTranslatedBlocks(undefined);
          setTranslatedContent(undefined);
        }
        return;
      }
      const targetLangCode = LANGUAGE_CODES[currentLanguage];
      if (article.title) {
        const newTitle = await translateText(article.title, targetLangCode);
        setTranslatedTitle(newTitle);
      }
      if (article.lead) {
        const newLead = await translateText(article.lead, targetLangCode);
        setTranslatedLead(newLead);
      }
      if (article.blocks && article.blocks.length > 0) {
        const newBlocks = await translateBlockContent(article.blocks, targetLangCode);
        setTranslatedBlocks(newBlocks);
      } else if (article.content && typeof article.content === 'string') {
        const newContent = await translateText(article.content, targetLangCode);
        setTranslatedContent(newContent);
      }
    };
    handleTranslation();
  }, [article, currentLanguage, translateText, translateBlockContent, isTranslating]);

  const handleLanguageChange = (language: SupportedLanguage) => {
    if (language === currentLanguage) return;
    setCurrentLanguage(language);
    if (language === 'English') {
      toast({
        title: "Original language restored",
        description: "Viewing content in its original language"
      });
    } else {
      toast({
        title: `Translating to ${language}`,
        description: "Translation powered by DeepL"
      });
    }
  };

  if (isLoading || isTranslating) {
    return <ArticleLoading />;
  }

  if (error) {
    return <ArticleError error={error} />;
  }

  const displayArticle = article || {
    id: articleId || '1',
    title: "The Future of Renewable Energy in Urban Settings",
    lead: "A comprehensive look at how cities are adapting to sustainable energy solutions.",
    content: `
      <p>The transition to renewable energy sources is one of the most pressing challenges of our time. As urban populations continue to grow, the demand for clean, sustainable energy solutions becomes increasingly urgent.</p>
      
      <h2>The Urban Energy Challenge</h2>
      <p>Cities account for approximately 75% of global primary energy consumption and are responsible for 70% of greenhouse gas emissions. The dense concentration of buildings, transportation systems, and industrial activities in urban areas presents both challenges and opportunities for renewable energy implementation.</p>
      
      <figure>
        <img src="/placeholder.svg" alt="Urban solar panels on rooftops" />
        <figcaption>Solar panels installed on urban rooftops in Barcelona, Spain.</figcaption>
      </figure>
      
      <p>Traditional energy infrastructure in cities was designed around centralized power generation and distribution. However, the renewable energy paradigm often favors distributed generation, where power is produced closer to where it's consumed.</p>
      
      <h2>Innovative Solutions</h2>
      <p>Several innovative approaches are emerging to address these challenges:</p>
      
      <h3>Building-Integrated Photovoltaics (BIPV)</h3>
      <p>BIPV systems incorporate solar power generation directly into building materials, such as windows, facades, and roofing. This approach not only generates electricity but also reduces the overall environmental footprint of buildings.</p>
      
      <h3>Urban Wind Energy</h3>
      <p>While traditional wind turbines may not be suitable for dense urban environments, new designs specifically tailored for cities are being developed. These include vertical-axis wind turbines and small-scale rooftop installations that can operate efficiently in variable wind conditions.</p>
      
      <h3>Waste-to-Energy Systems</h3>
      <p>Cities produce enormous amounts of waste that can be converted into energy through processes such as anaerobic digestion and thermal conversion. These systems not only generate renewable energy but also help manage waste streams more effectively.</p>
      
      <h2>Policy and Implementation</h2>
      <p>Successful integration of renewable energy in urban settings requires supportive policies, community engagement, and innovative financing mechanisms. Cities like Copenhagen, Amsterdam, and Melbourne have set ambitious targets for carbon neutrality and are implementing comprehensive strategies to achieve these goals.</p>
      
      <p>As we continue to develop and refine these approaches, the vision of sustainable, resilient urban energy systems becomes increasingly attainable. The future of our cities—and our planet—depends on our ability to transform this vision into reality.</p>
    `,
    header_image_url: "/placeholder.svg",
    author: {
      id: '1',
      name: "Emma Johnson",
      bio: "Environmental journalist focusing on urban sustainability and renewable energy solutions",
      avatar: "/placeholder.svg",
      isFollowed: false,
      subscriptionPrice: 5.99
    },
    publishedAt: "2023-05-15T10:30:00Z",
    readTime: 8,
    topics: ["Environment", "Technology", "Urban Planning"],
    hasAudio: true,
    hasTranslations: true,
    likes: 238,
    comments: 42,
    views: 1892
  };

  const displayTags = displayArticle.topics || displayArticle.tags || [];
  const displayReadTime = calculatedReadTime || displayArticle.readTime || displayArticle.reading_time || 1;
  const displayTitle = translatedTitle || displayArticle.title;
  const displayLead = translatedLead || displayArticle.lead;
  const articlePrice = displayArticle.price || 4.99;

  return <ArticleAIProvider article={displayArticle}>
      <div className="min-h-screen bg-background">
        <ArticleHeaderImage imageUrl={displayArticle.header_image_url} title={displayTitle} />
        
        <ArticleHeader title={displayTitle} topics={displayTags} author={displayArticle.author || {}} publishedAt={displayArticle.publishedAt || new Date().toISOString()} readTime={displayReadTime} isFollowing={isFollowing} setIsFollowing={setIsFollowing} />

        <ArticleLead lead={displayLead} />
        
        <div className="max-w-3xl mx-auto px-4 md:px-0">
          <ArticleToolbar articleId={displayArticle.id.toString()} hasAudio={displayArticle.hasAudio} hasTranslations={false} currentLanguage={currentLanguage} setCurrentLanguage={setCurrentLanguage} isBookmarked={isBookmarked} toggleBookmark={toggleBookmark} />
          
          <ArticleAIPanel article={displayArticle} />
        </div>

        <EnhancedArticleContent content={translatedContent || displayArticle.content} editorData={displayArticle.editor_data} blocks={translatedBlocks || displayArticle.blocks} authorId={displayArticle.author?.id} />
        
        <ArticleFooter 
          articleId={displayArticle.id.toString()} 
          likes={displayArticle.likes} 
          comments={displayArticle.comments} 
          author={{
            id: displayArticle.author?.id,
            name: displayArticle.author?.name || 'Unknown Author',
            subscriptionPrice: displayArticle.author?.subscriptionPrice
          }}
          articlePrice={articlePrice}
        />
        
        {displayArticle.hasTranslations && <InstantTranslation currentLanguage={currentLanguage} onLanguageChange={handleLanguageChange} />}
      </div>
    </ArticleAIProvider>;
};

export default ArticleReader;
